import axios from 'axios';
 
import { Trip } from '../types/trip';
import { TripDetail } from '../types/tripDetail';
 
// Base URL for the operator API
const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
 
// Create axios instance with default config
const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  timeout: 10000, // 10 seconds timeout
});

// Simple request cache to prevent duplicate calls
interface CacheItem {
  data: any;
  timestamp: number;
  promise?: Promise<any>;
}

const requestCache: Record<string, CacheItem> = {};
const CACHE_TTL = 30000; // 30 seconds cache lifetime

// Helper function to get/set from cache
const withCache = async <T>(cacheKey: string, fetchFn: () => Promise<T>, ttl = CACHE_TTL): Promise<T> => {
  const now = Date.now();
  const cached = requestCache[cacheKey];
  
  // If we have a valid cache entry, return it
  if (cached && (now - cached.timestamp < ttl)) {
    
    // If there's already a pending promise, return that instead of the cached data
    if (cached.promise) {
      return cached.promise;
    }
    
    return cached.data;
  }
  
  // If there's already a request in flight, return its promise
  if (cached?.promise) {
    return cached.promise;
  }
  
  // Otherwise, make the request and cache it
  
  // Store the promise in the cache
  const promise = fetchFn();
  requestCache[cacheKey] = {
    data: null,
    timestamp: now,
    promise
  };
  
  try {
    // Wait for the request to complete
    const data = await promise;
    
    // Update the cache with the actual data
    requestCache[cacheKey] = {
      data,
      timestamp: now,
    };
    
    return data;
  } catch (error) {
    // If the request fails, remove it from the cache
    delete requestCache[cacheKey];
    throw error;
  }
};
 
// Authentication token handler
let authToken: string | null = null;
 
// Set auth token
const setAuthToken = (token: string) => {
  authToken = token;
  apiClient.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};
 
// Clear auth token
const clearAuthToken = () => {
  authToken = null;
  delete apiClient.defaults.headers.common['Authorization'];
};
 
// Simple API client with straightforward methods based on the OpenAPI spec
export const api = {
  // Authentication
  login: async (mobileNumber: string) => {
    try {
      const response = await apiClient.post('/api/auth/send-otp', { mobileNumber, countryCode: '91' });
      return response.data;
    } catch (error) {
      console.error('Error during login:', error);
      throw error;
    }
  },
 
  verifyOtp: async (mobileNumber: string, otp: string) => {
    try {
      const response = await apiClient.post('/api/auth/verify-otp', { mobileNumber, otp, countryCode: '91', email: '', isCustomerApp: false });
      // Check if response contains token and handle it safely
      const authResponse = response.data as { token?: string, user?: any };
      if (authResponse.token) {
        setAuthToken(authResponse.token);
      }
      return response.data;
    } catch (error) {
      console.error('Error verifying OTP:', error);
      throw error;
    }
  },
   // Cars
  getCars: async () => {
    return withCache('getCars', async () => {
      try {
        // Call the endpoint that gets all cars 
        const response = await apiClient.get('/api/cars');
        const responseData = response.data as { data: any[]; message: string; status: boolean };
        const carData = responseData.data || []; 
   // Return the direct array of cars
        return carData;
      } catch (error) {
        console.error('Error fetching all cars:', error);
        throw error;
      }
    });
  },
 
  updateCar: async (id: number, car: any) => {
    try {
      const response = await apiClient.put(`/api/cars/${id}`, car);
      return response.data;
    } catch (error) {
      console.error(`Error updating car ${id}:`, error);
      throw error;
    }
  },
 
  deleteCar: async (id: number) => {
    try {
      await apiClient.delete(`/api/cars/${id}`);
      return true;
    } catch (error) {
      console.error(`Error deleting car ${id}:`, error);
      throw error;
    }
  },
 
  addCar: async (car: any) => {
    try {
      const response = await apiClient.post('/api/cars', car);
      return response.data;
    } catch (error) {
      console.error('Error adding car:', error);
      throw error;
    }
  },
   // Drivers
  getDrivers: async () => {
    return withCache('getDrivers', async () => {
      try {
        const response = await apiClient.get('/api/operator/trips/drivers/available');       
        const responseData = response.data as { data: any[]; message: string; status: boolean };
        const driverData = responseData.data || [];        
        // Return the direct array of drivers
        return driverData;
      } catch (error) {
        console.error('Error fetching drivers:', error);
        throw error;
      }
    });
  },
 
  updateDriver: async (id: number, driver: any) => {
    try {
      const response = await apiClient.put(`/api/driver/${id}`, driver);
      return response.data;
    } catch (error) {
      console.error(`Error updating driver ${id}:`, error);
      throw error;
    }
  },
 
  deleteDriver: async (id: number) => {
    try {
      const response = await apiClient.delete(`/api/driver/${id}`);
      // Clear the drivers cache to ensure fresh data on next fetch
      delete requestCache['getDrivers'];
      return response.data || { message: 'Driver deleted successfully' };
    } catch (error) {
      console.error(`Error deleting driver ${id}:`, error);
      throw error;
    }
  },
 
  addDriver: async (driver: any) => {
    try {
      const response = await apiClient.post('/api/driver', driver);
      // Clear the drivers cache to ensure fresh data on next fetch
      delete requestCache['getDrivers'];
      return response.data;
    } catch (error) {
      console.error('Error adding driver:', error);
      throw error;
    }
  },


 
  // Trips
  createTrip: async (tripData: {
    customerId: number;
    travelServiceDetailId: number;
    sourceCity: string;
    destinationCities: string;
    startDatetime: string;
  }) => {
    try {
      const response = await apiClient.post('/api/operator/trips', tripData);
      return response.data;
    } catch (error) {
      console.error('Error creating trip:', error);
      throw error;
    }
  },
  getAllTrips: async () => {
    // return withCache('getAllTrips', async () => {
      try {
        const response = await apiClient.get('/api/operator/trips/all');
        return response.data;
      } catch (error) {
        console.error('Error fetching all trips:', error);
        throw error;
      }
    // });
  },
  // Callback Requests
  getPendingCallbacks: async () => {
    try {
      const response = await apiClient.get('/api/operator/callbacks/pending');
      return response.data;
    } catch (error) {
      console.error('Error fetching pending callbacks:', error);
      throw error;
    }
  },

  updateCallbackStatus: async (callbackId: number, status: string, notes?: string) => {
    try {
      const requestBody = {
        id: callbackId,
        status: status,
        notes: notes || ''
      };
      
      const response = await apiClient.put('/api/operator/callbacks/status', requestBody);
      
      return response.data;
    } catch (error: any) {
      console.error('Error updating callback status:', error);
      if (error.response) {
        console.error('Error response data:', error.response.data);
        console.error('Error response status:', error.response.status);
      }
      throw error;
    }
  },

  getTripDetails: async (tripId: string) => {
    try {
      const response = await apiClient.get(`/api/operator/trips/${tripId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching trip details for ${tripId}:`, error);
      throw error;
    }
  },
 
  // Trip Information
  updateTripInformation: async (tripId: string, tripDetails: {
    travelServiceDetailId?: number;
    sourceCity: string;
    destinationCities: string;
    pickupAddress: string;
    dropAddress: string;
    pickupDate: string;
    returnDate: string;
    notes: string;
  }) => {
    try {
      const response = await apiClient.put(`/api/operator/trips/${tripId}`, tripDetails);
      return response.data;
    } catch (error) {
      console.error(`Error updating trip information for ${tripId}:`, error);
      throw error;
    }
  },

  // Update Trip Service Details (new API endpoint)
  updateTripServiceDetails: async (tripCode: string, serviceDetails: {
    customerId: number;
    travelServiceDetailId: number;
    sourceCity: string;
    destinationCities: string;
    startDatetime: string;
    endDatetime: string;
    request: string;
    notes: string;
    pricing: {
      ratePerDay: number;
      ratePerExtraKm: number;
      ratePerExtraHour: number;
      driverAllowancePerDay: number;
      estimatedTripFare: number;
      discount: number;
      noOfDays: number;
      agreedTripFare: number;
    };
  }) => {
    try {
      const response = await apiClient.post(`/api/operator/trips/${tripCode}/service-details`, serviceDetails);
      return response.data;
    } catch (error) {
      console.error(`Error updating trip service details for ${tripCode}:`, error);
      throw error;
    }
  },
 
  // Vehicle Details
  updateVehicleInformation: async (tripId: string, vehicleDetails: any) => {
    try {
      const response = await apiClient.put(`/api/trips/${tripId}/vehicle`, vehicleDetails);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
 
  // Customer Details
  updateCustomerInformation: async (tripId: string, customerDetails: any) => {
    try {
      const response = await apiClient.put(`/api/trips/${tripId}/customer`, customerDetails);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
 
  // Passenger Details
  updatePassengerInformation: async (tripId: string, passengerDetails: any) => {
    try {
      const response = await apiClient.put(`/api/trips/${tripId}/passenger`, passengerDetails);
      return response.data;
    } catch (error) {
      throw error;
    }
  },
 
  // Driver Assignment
  updateDriverInformation: async (tripId: string, driverDetails: any) => {
    try {
      const response = await apiClient.put(`/api/trips/${tripId}/driver`, driverDetails);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Trip Extra Charges
  getTripExtraCharges: async (tripId: string) => {
    try {
      const response = await apiClient.get(`/api/trips/${tripId}/extra-charges`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching extra charges for trip ${tripId}:`, error);
      throw error;
    }
  },

  // Add Trip Extra Charge
  addTripExtraCharge: async (tripCode: string, extraChargeData: { chargeType: string; amount: number; notes: string; }) => {
    try {  
      const response = await apiClient.post(`/api/operator/trips/${tripCode}/extra-charges`, extraChargeData);
      
      return response.data;
    } catch (error) {
      console.error(`Error adding extra charge for trip ${tripCode}:`, error);
      throw error;
    }
  },
  deleteTripExtraCharge: async (tripCode: string, extraChargeData: { id: number; }) => {
    try {  
      const response = await apiClient.delete(`/api/operator/trips/${tripCode}/extra-charges/${extraChargeData.id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting extra charge for trip ${tripCode}:`, error);
      throw error;
    }
  },

  // Update Trip Extra Charge
  updateTripExtraCharge: async (tripCode: string, chargeId: number, extraChargeData: { chargeType: string; amount: number; notes: string; }) => {
    try {  
      const response = await apiClient.put(`/api/operator/trips/${tripCode}/extra-charges/${chargeId}`, extraChargeData);
      return response.data;
    } catch (error) {
      console.error(`Error updating extra charge for trip ${tripCode}:`, error);
      throw error;
    }
  },
  
  // Trip Feedback
  getTripFeedback: async (tripId: string) => {
    try {
      const response = await apiClient.get(`/api/operator/trips/${tripId}/feedback`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching feedback for trip ${tripId}:`, error);
      throw error;
    }
  },

  // Add Trip Feedback
  addTripFeedback: async (tripId: string, feedbackData: any) => {
    try {
      const response = await apiClient.post(`/api/operator/trips/${tripId}/feedback`, feedbackData);
      return response.data;
    } catch (error) {
      console.error(`Error adding trip feedback for ${tripId}:`, error);
      throw error;
    }
  },

  // Trip Comments
  getTripComments: async (tripId: string) => {
    try {
      const response = await apiClient.get(`/api/operator/trips/${tripId}/comments`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching comments for trip ${tripId}:`, error);
      throw error;
    }
  },

  // Update Trip Comments
  updateTripComments: async (tripCode: string, comment:string) => {
    try {  
      const response = await apiClient.post(
        `/api/operator/trips/${tripCode}/comments`,
        comment,
        { headers: { 'Content-Type': 'application/json' } }
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating trip comments for ${tripCode}:`, error);
      throw error;
    }
  },

  // Update Trip Passengers
  updateTripPassengers: async (tripId: string, passengersData: any[]) => {
    try {
      const response = await apiClient.post(`/api/operator/trips/${tripId}/passengers`, { passengers: passengersData });
      return response.data;
    } catch (error) {
      console.error(`Error updating trip passengers for ${tripId}:`, error);
      throw error;
    }
  },

  // Update Trip Pricing
  updateTripPricing: async (tripId: string, pricingData: any) => {
    try {
      const response = await apiClient.post(`/api/operator/trips/${tripId}/pricing`, pricingData);
      return response.data;
    } catch (error) {
      console.error(`Error updating trip pricing for ${tripId}:`, error);
      throw error;
    }
  },

  // Add Trip Expense
  addTripExpense: async (tripCode: string, expenseData: any) => {
    try {
      const response = await apiClient.post(`/api/operator/trips/${tripCode}/expenses`, expenseData);
      return response.data;
    } catch (error) {
      console.error(`Error adding trip expense for ${tripCode}:`, error);
      throw error;
    }
  },

  // Update Trip Expense
  updateTripExpense: async (tripCode: string, expenseId: number, expenseData: any) => {
    try {
      const response = await apiClient.put(`/api/operator/trips/${tripCode}/expenses/${expenseId}`, expenseData);
      return response.data;
    } catch (error) {
      console.error(`Error updating trip expense for ${tripCode}:`, error);
      throw error;
    }
  },

  // Delete Trip Expense
  deleteTripExpense: async (tripCode: string, expenseId: number) => {
    try {
      const response = await apiClient.delete(`/api/operator/trips/${tripCode}/expenses/${expenseId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting expense for trip ${tripCode}:`, error);
      throw error;
    }
  },


  // Payment Details
  addPaymentInformation: async (tripId: string, paymentDetails: any) => {
    try {
      const response = await apiClient.post(`/api/trips/${tripId}/payments`, paymentDetails);
      return response.data;
    } catch (error) {
      throw error;
    }
  },

  // Razorpay Payment Flow
  createPaymentOrder: async (orderData: { tripUuid: string, amount: number, currency: string, notes: string }) => {
    try {
      
      const response = await apiClient.post('/api/payments/create-order', orderData);
      
      return response.data;
    } catch (error: any) {
      console.error('API: Error creating payment order:', error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('API: Error response data:', error.response.data);
        console.error('API: Error response status:', error.response.status);
        throw new Error(error.response.data?.message || `Server error: ${error.response.status}`);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('API: No response received:', error.request);
        throw new Error('No response from server. Please check your network connection.');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('API: Request setup error:', error.message);
        throw error;
      }
    }
  },

  verifyPayment: async (paymentData: { orderId: string, paymentId: string, signature: string, tripUuid: string }) => {
    try {
      
      const response = await apiClient.post('/api/payments/verify-payment', paymentData);
      
      return response.data;
    } catch (error: any) {
      console.error('API: Error verifying payment:', error);
      if (error.response) {
        // The request was made and the server responded with a status code
        // that falls out of the range of 2xx
        console.error('API: Error response data:', error.response.data);
        console.error('API: Error response status:', error.response.status);
        throw new Error(error.response.data?.message || `Server error: ${error.response.status}`);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('API: No response received:', error.request);
        throw new Error('No response from server. Please check your network connection.');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('API: Request setup error:', error.message);
        throw error;
      }
    }
  },

  // Create payment link
  createPaymentLink: async (tripId: string, paymentLinkData: { 
    amount: number, 
    currency: string, 
    customerName: string, 
    customerMobile: string, 
    customerEmail?: string, 
    customerCountryCode: string, 
    description: string, 
    validityInMinutes?: number 
  }) => {
    try {
      
      const response = await apiClient.post(`/api/operator/trips/${tripId}/payment/generate-link`, paymentLinkData);
      
      return response.data;
    } catch (error: any) {
      console.error('API: Error creating payment link:', error);
      if (error.response) {
        console.error('API: Error response data:', error.response.data);
        console.error('API: Error response status:', error.response.status);
        throw new Error(error.response.data?.message || `Server error: ${error.response.status}`);
      } else if (error.request) {
        console.error('API: No response received:', error.request);
        throw new Error('No response from server. Please check your network connection.');
      } else {
        console.error('API: Request setup error:', error.message);
        throw error;
      }
    }
  },

  // Process cash payment
  processCashPayment: async (tripId: string, cashPaymentData: { 
    amount: number, 
    notes?: string | null, 
    customerName?: string, 
    customerMobile?: string, 
    refNumber?: string ,
    paymentMode?: string,
    cashUserId?: number,
    cashTrnDate?: string
  }) => {
    try {
      
      const response = await apiClient.post(`/api/operator/trips/${tripId}/payment/pay`, cashPaymentData);
      
      return response.data;
    } catch (error: any) {
      console.error('API: Error processing cash payment:', error);
      if (error.response) {
        console.error('API: Error response data:', error.response.data);
        console.error('API: Error response status:', error.response.status);
        throw new Error(error.response.data?.message || `Server error: ${error.response.status}`);
      } else if (error.request) {
        console.error('API: No response received:', error.request);
        throw new Error('No response from server. Please check your network connection.');
      } else {
        console.error('API: Request setup error:', error.message);
        throw error;
      }
    }
  },

  setCashToUser: async (transactionId: number,cashInHandTo: string, trnDate: string) => {
  try {
    const response = await apiClient.put(`/api/operator/setCashToUser/${transactionId}?cashInHandTo=${cashInHandTo}&trnDate=${trnDate}`);
    return response.data;
  } catch (error: any) {
    console.error('Error processing cash to operator:', error);
    throw error;
  }
},

  // Check payment link status
  checkPaymentLinkStatus: async (tripId: string, paymentLinkId: string) => {
    try { 
      // Make GET request to check payment status
      const response = await apiClient.get(`/api/operator/trips/${tripId}/payment/link/${paymentLinkId}/status`); 
      return response.data;
    } catch (error: any) {
      console.error('API DEBUG: Error checking payment link status:', error);
      
      // Log detailed error information
      if (error.response) {
        // The request was made and the server responded with a status code outside of 2xx range
        console.error('API DEBUG: Error response data:', error.response.data);
        console.error('API DEBUG: Error response status:', error.response.status);
        console.error('API DEBUG: Error response headers:', error.response.headers);
        throw new Error(error.response.data?.message || `Server error: ${error.response.status}`);
      } else if (error.request) {
        // The request was made but no response was received
        console.error('API DEBUG: No response received. Request details:', error.request);
        throw new Error('No response from server. Please check your network connection.');
      } else {
        // Something happened in setting up the request that triggered an Error
        console.error('API DEBUG: Request setup error:', error.message);
        throw error;
      }
    }
  },

  // Process refund
  processRefund: async (refundData: { 
    customerName: string,
    customerMobile: string,
    customerCountryCode: string,
    paymentId: string, 
    tripUuid: string, 
    amount: number, 
    notes: string, 
    refundReason: string | null 
  }) => {
    try {
      
      const response = await apiClient.post('/api/operator/payment/refund', refundData, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error processing refund:', error);
      throw error;
    }
  },

  // Process cash refund
  processCashRefund: async (cashRefundData: { 
    transactionId: number,
    amount: number, 
    tripCode: string, 
    customerName: string, 
    customerMobile: string, 
    customerCountryCode: string, 
    refundReason: string | null, 
    notes: string, 
    refNumber?: string
  }) => {
    try {
      
      const response = await apiClient.post('/api/operator/payment/cash/refund', cashRefundData, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error processing cash refund:', error);
      throw error;
    }
  },

  // Get trip transactions
  getTripTransactions: async (tripId: string) => {
    try {
      const response = await apiClient.get(`/api/operator/trips/${tripId}/transactions`, {
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching trip transactions:', error);
      throw error;
    }
  },
   // Token management
  setAuthToken,
  clearAuthToken,
  
  // Cache management
  clearCache: (key?: string) => {
    if (key) {
      delete requestCache[key];
      
    } else {
      Object.keys(requestCache).forEach(k => delete requestCache[k]);
      
    }
  },
   // Pricing
  getPricingTable: async () => {
    return withCache('getPricingTable', async () => {
      try {
        const response = await apiClient.get('/api/operator/tariff/pricing-table');
        return response.data;
      } catch (error) {
        console.error('Error fetching pricing table:', error);
        throw error;
      }
    });
  },

  searchTrips: async (params: {
    dateType: 'PICKUP' | 'BOOKING';
    fromDate: string;
    toDate: string;
    carTypeId: number;
    statuses: string[];
    tripTypes: string[];
    leadSources: string[];
  }) => {
    try {
      const searchParams = new URLSearchParams({
        dateType: params.dateType,
        fromDate: params.fromDate,
        toDate: params.toDate,
        carTypeId: params.carTypeId.toString(),
        statuses: params.statuses.join(','),
        tripTypes: params.tripTypes.join(','),
        leadSources: params.leadSources .join(',')
      });
      
      const response = await apiClient.get(`/api/operator/trips/search?${searchParams}`);
      return response.data;
    } catch (error) {
      console.error('Error searching trips:', error);
      throw error;
    }
  }, 
  
  // Customer lookup or create
  lookupOrCreateCustomer: async (mobileNumber: string,countryCode: String) => {
    try {
      const response = await apiClient.post('/api/operator/customer/lookup-or-create', { mobileNumber,countryCode });
      return response.data;
    } catch (error) {
      console.error('Error looking up or creating customer:', error);
      throw error;
    }
  },

  // Update customer
  updateCustomer: async (customerId: number, customerData: { name: string; email: string; gender: string; address: string; }) => {
    try {
      const response = await apiClient.put(`/api/operator/customer/${customerId}`, customerData);
      return response.data;
    } catch (error) {
      console.error('Error updating customer:', error);
      throw error;
    }
  },

  // Utility to check if user is authenticated
  isAuthenticated: () => !!authToken,
  // Contact Requests
  getContactRequests: async () => {
    try { 
      const response = await apiClient.get('/api/operator/contact-requests');
      return response.data;
    } catch (error: any) {
      console.error('API: Error fetching contact requests:', error);
      if (error.response) {
        console.error('API: Error response status:', error.response.status);
        console.error('API: Error response data:', error.response.data);
        console.error('API: Error response URL:', error.config?.url);
      }
      throw error;
    }
  },

  updateContactRequestStatus: async (contactId: number, status: string, response?: string, contactData?: any) => {
    try {
      const requestBody = {
        id: contactId,
        name: contactData?.name || '',
        status: status,
        response: response || ''
      };
      
      
      const apiResponse = await apiClient.put(`/api/operator/contact-requests/${contactId}`, requestBody);
      
      return apiResponse.data;
    } catch (error: any) {
      console.error('API: Error updating contact request status:', error);
      if (error.response) {
        console.error('API: Error response data:', error.response.data);
        console.error('API: Error response status:', error.response.status);
        console.error('API: Error response URL:', error.config?.url);
      }
      throw error;
    }
  },  // Driver API for trips
  getAllDrivers: async () => {
    return withCache('getAllDrivers', async () => {
      try {
        // Call the endpoint that gets all drivers 
        const response = await apiClient.get('/api/operator/trips/drivers/available');    
        // Ensure we're returning a consistent data structure
        const result = {
          data: response.data
        };
        
        // Add type assertion to help TypeScript understand this is a valid structure
        return result as { data: any };
      } catch (error) {
        console.error('Error fetching all drivers:', error);
        throw error;
      }
    });
  },
  assignDriverToTrip: async (tripCode: string, driverId: number) => {
    try {
      
      const response = await apiClient.put(`/api/operator/trips/${tripCode}/driver/${driverId}`);
      return response.data;
    } catch (error) {
      console.error(`Error assigning driver ${driverId} to trip ${tripCode}:`, error);
      throw error;
    }
  },
  getAllCars: async () => {
    return withCache('getAllCars', async () => {
      try {
        // Call the endpoint that gets all cars 
        const response = await apiClient.get('/api/cars');
        
        // Ensure we're returning a consistent data structure
        const result = {
          data: response.data
        };
        
        // Add type assertion to help TypeScript understand this is a valid structure
        return result as { data: any };
      } catch (error) {
        console.error('Error fetching all cars:', error);
        throw error;
      }
    });
  },
  assignCarToTrip: async (tripCode: string, carId: number) => {
    try {
      
      const response = await apiClient.put(`/api/operator/trips/${tripCode}/car/${carId}`);
      return response.data;
    } catch (error) {
      console.error(`Error assigning car ${carId} to trip ${tripCode}:`, error);
      throw error;
    }
  },
  // Add this after the assignCarToTrip method:
  removeCarFromTrip: async (tripCode: string) => {
    try {
      
      const response = await apiClient.delete(`/api/operator/trips/${tripCode}/car`);
      return response.data;
    } catch (error) {
      console.error(`Error removing car from trip ${tripCode}:`, error);
      throw error;
    }
  },

  // Remove driver from trip
  removeDriverFromTrip: async (tripCode: string) => {
    try { 
      const response = await apiClient.delete(`/api/operator/trips/${tripCode}/driver`);
      return response.data;
    } catch (error) {
      console.error(`Error removing car from trip ${tripCode}:`, error);
      throw error;
    }
  },

  checkCarAvailability: async(tripCode:String,carTypeId:number,carId:number)=>{
    try{
      const response = await apiClient.get('/api/cars/available',{
          params:{
            tripCode,
            carTypeId,
            carId
          }
      });
      return response.data;
    } catch(error){
      console.error(`Error checking car availability for trip ${tripCode}:`, error);
      throw error;
    }
  },

  tripHistory: async (tripId: string) => {
    try {
      const response = await apiClient.get(`/api/operator/trips/${tripId}/status-history`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching trip history for trip ${tripId}:`, error);
      throw error;
    }
  },

  // Add new trip passenger
  addTripPassenger: async (tripCode: string, passengerData: any) => {
    try {
      
      const response = await apiClient.post(`/api/operator/trips/${tripCode}/passengers`, passengerData);
      return response.data;
    } catch (error) {
      console.error(`Error adding passenger to trip ${tripCode}:`, error);
      throw error;
    }
  },

  // Update existing trip passenger
  updateTripPassenger: async (tripCode: string, passengerId: number, passengerData: any) => {
    try {
      
      const response = await apiClient.put(`/api/operator/trips/${tripCode}/passengers/${passengerId}`, passengerData);
      return response.data;
    } catch (error) {
      console.error(`Error updating passenger ${passengerId} for trip ${tripCode}:`, error);
      throw error;
    }
  },

  // Remove trip passenger
  removeTripPassenger: async (tripCode: string, passengerId: number) => {
    try {
      
      const response = await apiClient.delete(`/api/operator/trips/${tripCode}/passengers/${passengerId}`);
      return response.data;
    } catch (error) {
      console.error(`Error removing passenger ${passengerId} from trip ${tripCode}:`, error);
      throw error;
    }
  },

  // Trip Address Management
  // Add new trip address
  addTripAddress: async (tripCode: string, addressData: {
    addressType: 'PICKUP' | 'DROP';
    address: string | null;
    city: string;
    state: string;
    postalCode: string;
    landmark?: string;
    latitude?: number;
    longitude?: number;
  }) => {
    try {
      
      const response = await apiClient.post(`/api/operator/trips/${tripCode}/addresses`, addressData);
      return response.data;
    } catch (error) {
      console.error(`Error adding address to trip ${tripCode}:`, error);
      throw error;
    }
  },

  // Update existing trip address
  updateTripAddress: async (tripCode: string, addressId: number, addressData: {
    addressType: 'PICKUP' | 'DROP';
    address: string | null;
    city: string;
    state: string;
    postalCode: string;
    landmark?: string;
    latitude?: number;
    longitude?: number;
  }) => {
    try {
      
      const response = await apiClient.put(`/api/operator/trips/${tripCode}/addresses/${addressId}`, addressData);
      return response.data;
    } catch (error) {
      console.error(`Error updating address ${addressId} for trip ${tripCode}:`, error);
      throw error;
    }
  },

  // Remove trip address
  removeTripAddress: async (tripCode: string, addressId: number) => {
    try {
      
      const response = await apiClient.delete(`/api/operator/trips/${tripCode}/addresses/${addressId}`);
      return response.data;
    } catch (error) {
      console.error(`Error removing address ${addressId} from trip ${tripCode}:`, error);
      throw error;
    }
  },

  updateTripStatus: async (tripCode: string, status: string) => {
    try {
      
      const response = await apiClient.put(`/api/operator/trips/${tripCode}/status`, { status });
      return response.data;
    } catch (error) {
      console.error(`Error updating trip status for trip ${tripCode}:`, error);
      throw error;
    }
  },

  // Cancel trip
  cancelTrip: async (tripCode: string, cancelData: { notes: string }) => {
    try {
      
      const response = await apiClient.put(
        `/api/operator/trips/${tripCode}/cancel?reason=${encodeURIComponent(cancelData.notes)}`,
        null,{
        headers: {
          Authorization: `Bearer ${authToken}`
        }
      });
      return response.data;
    } catch (error) {
      console.error(`Error cancelling trip ${tripCode}:`, error);
      throw error;
    }
  },

  // WhatsApp Notifications
  getWhatsAppTemplates: async () => {
    try {
      const response = await apiClient.get('/api/whatsapp/templates/metadata');
      return response.data;
    } catch (error) {
      console.error('Error fetching WhatsApp templates:', error);
      throw error;
    }
  },

  sendWhatsAppNotification: async (notificationData: {
    templateName: string;
    customerCountryCode: string;
    customerNumber: string;
    body: Record<string, string>;
  }) => {
    try {
      const response = await apiClient.post('/api/whatsapp/send', notificationData);
      return response.data;
    } catch (error) {
      console.error('Error sending WhatsApp notification:', error);
      throw error;
    }
  },

  // Invoice Generation
  generateTripInvoice: async (tripCode: string) => {
    try {
      
      const response = await apiClient.post(`/api/api/v1/invoices/generate/trip/code/${tripCode}`);
      
      return response.data;
    } catch (error) {
      console.error(`Error generating invoice for trip ${tripCode}:`, error);
      throw error;
    }
  },

  // Download Invoice PDF
  downloadInvoicePdf: async (invoiceId: string) => {
    try {
      
      const response = await apiClient.get(`/api/api/v1/invoices/${invoiceId}/export/pdf`, {
        responseType: 'blob' // Important for handling PDF download
      });
      
      return response.data;
    } catch (error) {
      console.error(`Error downloading PDF for invoice ${invoiceId}:`, error);
      throw error;
    }
  },

  getAllFlashDeals: async () => {
    try {
      const response = await apiClient.get('/api/flashdeals/all');
      return response.data;
    } catch (error) {
      console.error('Error fetching active flash deals:', error);
      throw error;
    }
  },

  getActiveFlashDeals : async ()=>{
    try {
      const response = await apiClient.get('/api/flashdeals/activeDeals');
      return response.data;
    }catch (error){
      console.error('Error fetching active flash deals:', error);
      throw error;
    }
  },

  getInActiveFlashDeals : async ()=>{
    try {
      const response = await apiClient.get('/api/flashdeals/inactiveDeals');
      return response.data;
    }catch (error){
      console.error('Error fetching active flash deals:', error);
      throw error;
    }
  },

  getcurrentFlasingDeals: async (atDate:string) => {
    try {
      const params = atDate ? `?atDate=${atDate}` : '';
      const response = await apiClient.get(`/api/flashdeals/activeAt${params}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching current flash deals:', error);
      throw error;
    }
  },

  getFlashDealByCode: async (dealCode: string) => {
    try {
      const response = await apiClient.get(`/api/flashdeals/code/${dealCode}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching flash deal with code ${dealCode}:`, error);
      throw error;
    }
  },

  createFlashDeal: async (flashDealData: any) => {
    try {
      const response = await apiClient.post('/api/flashdeals', flashDealData);
      return response.data;
    } catch (error) {
      console.error('Error creating flash deal:', error);
      throw error;
    }
  },

  updateFlashDeal: async (id: string, flashDealData: any) => {
    try {
      const response = await apiClient.put(`/api/flashdeals/${id}`, flashDealData);
      return response.data;
    } catch (error) {
      console.error(`Error updating flash deal with code ${id}:`, error);
      throw error;
    }
  },

  createZohoInvoice: async (tripCode:string) => {
    try{
      const response = await apiClient.get(`/api/trips/create-zoho-invoice?tripCode=${tripCode}`);
      return response.data;
    }catch(error){
      console.error(`Error creating Zoho invoice for trip ${tripCode}:`, error);
      throw error;
    }
  },

  // this will 
  getCashTrnsByInhand: async (cashInHand: string) => {
    try {
      const response = await apiClient.get(`/api/operator/GetCashTrnsByInhand/${cashInHand}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching cash transactions for ${cashInHand}:`, error);
      throw error;
    }
  },

  getCashBankTrnIds: async (cashInHand: string) => {
    try {
      const response = await apiClient.get(`/api/operator/GetCashBankTrnIds/${cashInHand}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching cash bank transaction IDs for ${cashInHand}:`, error);
      throw error;
    }
  },

  getCashTrnsByTrnId: async (trnId: string) => {
    try {
      const response = await apiClient.get(`/api/operator/GetCashTrnsByTrnId/${trnId}`);
      return response.data;
    } catch (error) {
      console.error(`Error fetching cash transaction for TRN ID ${trnId}:`, error);
      throw error;
    }
  },

  changeCashInHand: async (cashInHandData: {
    cashInHand: string;
    cashBankTrnId: string;
    cashTrnDate: string;
    transactionIds: number[];
  }) => {
    try {
      const response = await apiClient.put('/api/operator/ChangeCashInHand', cashInHandData);
      return response.data;
    } catch (error) {
      console.error('Error changing cash in hand:', error);
      throw error;
    }
  },

  processActiveNotifications: async (atDateTime:string) => {
    try{
      const response  = await apiClient.get(`/api/trips/ProcessActiveNotifications?atDatetime=${atDateTime}`)
      return response.data;
    }catch(error){
      console.error('Error processing active notifications:', error);
      throw error;
    }
  },

  getActiveNotifications: async (atDateTime:string) => {
    try{
      const response  = await apiClient.get(`/api/trips/GetActiveNotifications?atDatetime=${atDateTime}`)
      return response.data;
    }catch(error){
      console.error('Error fetching active notifications:', error);
      throw error;
    }
  },

  snoozeNotification: async (id: number, atDatetime: string) => {
  try {
    const response = await apiClient.put(`/api/trips/SnoozeNotification/${id}?atDatetime=${atDatetime}`)
    return response.data;
  } catch (error) {
    console.error(`Error snoozing notification ${id}:`, error);
    throw error;
  }
},

closeNotification: async (id: number) => {
  try {
    const response = await apiClient.put(`/api/trips/CloseNotification/${id}`)
    return response.data;
  } catch (error) {
    console.error(`Error closing notification ${id}:`, error);
    throw error;
  } 
},

modifyCashAmount: async(trnId: number, amount: number) => {
  try{
    const response  = await apiClient.put(`/api/operator/modifyPayment/${trnId}?amount=${amount}`);
    return response.data;
  }catch(error){
    console.error(`Error modifying cash amount for transaction ${trnId}:`, error);
    throw error;
  }
},

//omnicardrefund api
tripRefund: async(refundData:{
  tripCode: string;
  customerName: string;
  customerMobile: string;
  customerCountryCode: string;
  refundAmount: number;
  cancellationFee: number;
  refundTransactionId: string;
  reason: string | null;
})=>{
  try{
    const response = await apiClient.post('/api/operator/payment/TripRefund',refundData);
    return response.data;
  }catch(error){
    console.error('Error processing trip refund:', error);
    throw error;
  }
},

fetchBookings: async() => {
  try{
    const response  = await apiClient.get(`/api/operator/trips/GetInProgressAndFutureTrips`);
    return response.data;
  }catch(error){
    console.error(`Error in fetching bookings `, error);
    throw error;
  }
},

fetchTripTypes: async() => {
  try{
    const response  = await apiClient.get(`/api/public/services`);
    return response.data;
  }catch(error){
    console.error(`Error in fetching trip types `, error);
    throw error;
  }
},

getLeadConversionData: async(fromDateTime: string, toDateTime: string) => {
  try{
    const response  = await apiClient.get(`/api/trips/GetLeadConversionReport?fromDatetime=${fromDateTime}&toDatetime=${toDateTime}`);
    return response.data;
  }catch(error){
    console.error(`Error in fetching lead conversion data `, error);
    throw error;
  }
}

};

